import { usePlans, mergeLivePlans } from "@/lib/plans";
import TermsAcceptance, { useTermsAcceptance } from "@/components/TermsAcceptance";
import PpfQuestion from "@/components/PpfQuestion";
import { ppfToBool, boolToPpf } from "@/lib/vehicles";
import React, { lazy, Suspense, useEffect, useRef, useState } from "react";
import { useNavigate, useLocation, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Check, Smartphone, MapPin, CarFront, ArrowUpRight, MessageCircle, User, Phone, Mail, LockKeyhole, Eye, EyeOff, Car, Landmark, ShieldCheck, Copy, X, Droplets, Wrench, Armchair, Clock3, ChevronRight } from "lucide-react";
import { api, resolveMediaUrl, mediaStyle } from "@/lib/api";
import PolicyLinks from "@/components/PolicyLinks";
import { useAuth } from "@/lib/auth";
import PlanShowcase from "@/components/PlanShowcase";
import { PlanPrice, PriceBreakdown, intentToQuote, fetchQuote, errText, inr, usePlanTerms, bestTerm, TermPicker, PlanHoverPopup, PlanTag } from "@/lib/pricing";
import { Button } from "@/components/ui/button";
import BrandName from "@/components/BrandName";
import AuthModal from "@/components/AuthModal";
import UpcomingCities from "@/components/UpcomingCities";
import UpcomingServices from "@/components/UpcomingServices";
import Seo, { SITE } from "@/components/Seo";
import ReferAnimation from "@/components/ReferAnimation";
import BrandLoader from "@/components/BrandLoader";
import ServiceHeroes from "@/components/ServiceHeroes";
import SmoothModal from "@/components/SmoothModal";
import LaunchExperience from "@/components/LaunchExperience";
import { payWithCashfree, useCashfreeEnabled } from "@/lib/cashfree";
import { useGstBill, billFromOrder } from "@/lib/gst";

const GoogleReviews = lazy(() => import("@/components/GoogleReviews"));


/* ---- Instant first paint ----
 * The landing page used to render NOTHING until GET /api/content answered (a blank screen on slow phones).
 * Now: (1) the last answer is kept in localStorage and shown at once, (2) index.html starts the request before the
 * JavaScript bundle has even loaded (window.__naviContent), (3) with neither, the page shows its built-in defaults
 * straight away and swaps in the live content when it arrives. */
const CONTENT_CACHE_KEY = "navi_content_v1";
const FALLBACK_CONTENT = { __fallback: true, launch_experience: { enabled: false } };
function readCachedContent() {
  try {
    const raw = localStorage.getItem(CONTENT_CACHE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch { return null; }
}
function saveCachedContent(data) {
  try { localStorage.setItem(CONTENT_CACHE_KEY, JSON.stringify(data)); } catch { /* storage full or blocked: fine */ }
}

/** Respects prefers-reduced-motion globally, so autoplay/animation can back off for people who asked for it. */
function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduced(mq.matches);
    const onChange = (e) => setReduced(e.matches);
    mq.addEventListener ? mq.addEventListener("change", onChange) : mq.addListener(onChange);
    return () => (mq.removeEventListener ? mq.removeEventListener("change", onChange) : mq.removeListener(onChange));
  }, []);
  return reduced;
}

/** True on phones. Phones get full pages instead of popups. */
function useIsPhone() {
  const [phone, setPhone] = useState(() => typeof window !== "undefined" && window.matchMedia("(max-width: 639px)").matches);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 639px)");
    const on = (e) => setPhone(e.matches);
    mq.addEventListener ? mq.addEventListener("change", on) : mq.addListener(on);
    return () => (mq.removeEventListener ? mq.removeEventListener("change", on) : mq.removeListener(on));
  }, []);
  return phone;
}

/** Adds `.is-visible` to every `.navi-section-reveal` element the moment it scrolls into view
 *  (once), using IntersectionObserver so it's cheap and doesn't run on every scroll tick.
 *  threshold 0: a tall section (plans, gallery) can never show 15% of itself on a phone, which
 *  used to leave it invisible = a big empty gap. Any pixel entering the screen now reveals it. */
function useScrollReveal(reduced) {
  useEffect(() => {
    const els = document.querySelectorAll(".navi-section-reveal");
    if (reduced || !("IntersectionObserver" in window)) {
      els.forEach((el) => el.classList.add("is-visible"));
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0, rootMargin: "0px 0px -6% 0px" }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [reduced]);
}

/** Counts from 0 to `value` once, the first time it scrolls into view. */
function CountUp({ value, reduced, duration = 1600 }) {
  const ref = useRef(null);
  const target = Math.max(0, Number(value) || 0);
  const [n, setN] = useState(reduced ? target : 0);

  useEffect(() => {
    if (reduced || !("IntersectionObserver" in window)) { setN(target); return undefined; }
    const el = ref.current;
    if (!el) return undefined;
    let raf = 0;
    const io = new IntersectionObserver((entries) => {
      if (!entries.some((en) => en.isIntersecting)) return;
      io.disconnect();
      const start = performance.now();
      const tick = (now) => {
        const t = Math.min(1, (now - start) / duration);
        setN(Math.round(target * (1 - Math.pow(1 - t, 3))));
        if (t < 1) raf = requestAnimationFrame(tick);
      };
      raf = requestAnimationFrame(tick);
    }, { threshold: 0.4 });
    io.observe(el);
    return () => { io.disconnect(); cancelAnimationFrame(raf); };
  }, [target, reduced, duration]);

  return <span ref={ref}>{n.toLocaleString("en-IN")}</span>;
}

function StatsStrip({ stats, reduced }) {
  const items = (Array.isArray(stats?.items) ? stats.items : []).filter((it) => it && it.label);
  if (stats?.enabled === false || items.length === 0) return null;
  return (
    <div data-testid="stats-strip" className="mt-8 grid grid-cols-3 gap-3 sm:gap-4">
      {items.map((it, i) => (
        <div key={`${it.label}-${i}`} className="rounded-2xl border border-border bg-card px-3 py-4 sm:px-6 sm:py-5 text-center mac-shadow">
          <div className="text-2xl sm:text-4xl font-semibold tracking-tight text-primary tabular-nums">
            <CountUp value={it.value} reduced={reduced} />{it.suffix || ""}
          </div>
          <div className="mt-1 text-[0.6875rem] sm:text-sm text-muted-foreground">{it.label}</div>
        </div>
      ))}
    </div>
  );
}

/** Founder / co-founder name in the NAVI CARE wordmark style: Quicksand, lowercase, last word in brand blue. */
function BrandPerson({ role, name, testid }) {
  const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
  const last = parts.length > 1 ? parts.pop() : "";
  return (
    <div className="navi-founder">
      <div className="navi-founder-role">{role}</div>
      <div className="navi-founder-name" data-testid={testid} aria-label={name}>
        <span aria-hidden="true">{parts.join(" ")}</span>
        {last ? <b aria-hidden="true">{last}</b> : null}
      </div>
    </div>
  );
}

export default function Landing() {
  const cashfreeOn = useCashfreeEnabled();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { user, setUser } = useAuth();
  // Sign in / Create account is a popup over this page (no separate login page).
  const [authOpen, setAuthOpen] = useState(false);
  // Header "Book Now" menu: Car Care / Appliance Repair / Sofa Repair.
  const [bookOpen, setBookOpen] = useState(false);
  const bookRef = useRef(null);
  useEffect(() => {
    if (!bookOpen) return undefined;
    const onDown = (e) => { if (bookRef.current && !bookRef.current.contains(e.target)) setBookOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setBookOpen(false); };
    document.addEventListener("mousedown", onDown); document.addEventListener("touchstart", onDown); document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDown); document.removeEventListener("touchstart", onDown); document.removeEventListener("keydown", onKey); };
  }, [bookOpen]);
  const goBook = (path) => { setBookOpen(false); navigate(path); };
  const [authMode, setAuthMode] = useState("signin");
  const authRef = (searchParams.get("ref") || "").toUpperCase();
  const qrCodeParam = (searchParams.get("campaign") || searchParams.get("qr") || "").toUpperCase();
  const [qrCampaign, setQrCampaign] = useState(null);
  const [showQrPopup, setShowQrPopup] = useState(false);
  const openAuth = (mode = "signin") => { setAuthMode(mode); setAuthOpen(true); };
  useEffect(() => {
    if (!qrCodeParam) return;
    localStorage.setItem("navi_qr_code", qrCodeParam);
    api.post("/qr/track", { code: qrCodeParam }).then(({ data }) => {
      localStorage.setItem("navi_qr_campaign", qrCodeParam);
      if (data?.referral_profile_id) localStorage.setItem("navi_qr_referral_profile", data.referral_profile_id);
      setQrCampaign(data);
      if (data?.popup_enabled && localStorage.getItem(`navi_qr_dismissed_${qrCodeParam}`) !== "1") setShowQrPopup(true);
    }).catch(() => {});
  }, [qrCodeParam]);
  const closeAuth = () => { setAuthOpen(false); if (location.pathname === "/login") navigate("/", { replace: true }); };
  const hadUserOnMount = useRef(!!user);
  useEffect(() => {
    if (location.pathname !== "/login") return;
    if (hadUserOnMount.current) { navigate("/dashboard", { replace: true }); return; }
    setAuthMode(searchParams.get("mode") === "signup" ? "signup" : "signin");
    setAuthOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);
  const [content, setContent] = useState(() => readCachedContent() || FALLBACK_CONTENT);
  const { plans: livePlans } = usePlans();
  const terms = useTermsAcceptance();
  const [scrolled, setScrolled] = useState(false);
  const [, setActiveNav] = useState("");

  // In-page links (#how, #plans, ...): scroll so the section heading lands a
  // comfortable distance BELOW the fixed header + offer ticker, measured from the
  // real bars, so the heading never sits jammed under them.
  useEffect(() => {
    const onClick = (e) => {
      const a = e.target.closest && e.target.closest('a[href^="#"]');
      if (!a) return;
      const id = a.getAttribute("href").slice(1);
      const el = id && document.getElementById(id);
      if (!el) return;
      e.preventDefault();
      const bars = [document.querySelector(".navi-hero-nav"), document.querySelector(".navi-offer-fixed")];
      const chrome = Math.max(0, ...bars.map((b) => (b ? b.getBoundingClientRect().bottom : 0)));
      const padTop = parseFloat(getComputedStyle(el).paddingTop) || 0;
      const top = el.getBoundingClientRect().top + window.scrollY + Math.min(padTop, 40) - chrome - 24;
      window.scrollTo({ top: Math.max(0, top), behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth" });
      try { window.history.replaceState(null, "", `#${id}`); } catch {}
    };
    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, []);
  const [loaded, setLoaded] = useState(false);
  const [detailPlan, setDetailPlan] = useState(null);
  const [checkoutPlan, setCheckoutPlan] = useState(null);
  const [checkoutStep, setCheckoutStep] = useState("details");
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [checkoutShowPassword, setCheckoutShowPassword] = useState(false);
  const [checkoutIntent, setCheckoutIntent] = useState(null);
  const [checkoutQuote, setCheckoutQuote] = useState(null);
  const checkoutGst = useGstBill({ kind: "plan", ids: checkoutPlan?.id ? [checkoutPlan.id] : [], taxable: checkoutQuote ? checkoutQuote.subtotal : null });   // GST bill review (server-calculated)
  const [checkoutQuoteError, setCheckoutQuoteError] = useState("");
  const [checkoutMonths, setCheckoutMonths] = useState(1);
  const planTerms = usePlanTerms();
  const [refRate, setRefRate] = useState(10);
  useEffect(() => { api.get("/referrals/info").then(({ data }) => { if (Number(data?.commission_rate) > 0) setRefRate(Number(data.commission_rate)); }).catch(() => {}); }, []);          // 3/6/12-month options per plan (server-calculated)
  const [detailMonths, setDetailMonths] = useState(1);
  const [hover, setHover] = useState(null);  // { plan, rect, months } while the cursor rests on a plan card
  const hoverTimer = useRef(0);
  const [checkoutDuplicate, setCheckoutDuplicate] = useState(false);
  const [checkoutError, setCheckoutError] = useState("");
  const [checkoutForm, setCheckoutForm] = useState({ name: "", phone: "", email: "", pin: "", confirmPin: "", vehicleNumber: "", ppf: "", referralCode: "" });
  const [knownVehicles, setKnownVehicles] = useState(null);   // null = not loaded / guest
  const reducedMotion = usePrefersReducedMotion();
  // Which service the visitor chose in the hero: "car" (default) shows the car plans, "home" shows the home services and hides the car plans.
  const [serviceFocus, setServiceFocus] = useState("car");
  const [servicePick, setServicePick] = useState("");
  const chooseService = (focus, pick = "") => { setServiceFocus(focus === "home" ? "home" : "car"); setServicePick(pick || ""); };
  useEffect(() => {          // /#plans (menu link, other pages) always brings the car plans back
    const sync = () => { if (window.location.hash === "#plans") setServiceFocus("car"); };
    sync(); window.addEventListener("hashchange", sync);
    return () => window.removeEventListener("hashchange", sync);
  }, []);
  const isPhone = useIsPhone();

  useEffect(() => {
    let alive = true;
    const apply = (data) => {
      if (!alive || !data || typeof data !== "object") return;
      saveCachedContent(data);
      setContent((cur) => (cur && !cur.__fallback && JSON.stringify(cur) === JSON.stringify(data) ? cur : data)); // same answer = no re-render
    };
    const load = async () => {
      try {
        const { data } = await api.get(`/content`);
        apply(data);
      } catch {}
    };
    // The request index.html already started (runs while the bundle downloads): use its answer first.
    const early = typeof window !== "undefined" ? window.__naviContent : null;
    if (early && typeof early.then === "function") {
      window.__naviContent = null;
      early.then((data) => { if (data) apply(data); else load(); }).catch(load);
    } else load();
    // Poll briefly so an already-open public landing page reflects an Admin
    // upload/link change automatically.
    const id = window.setInterval(() => { if (document.visibilityState === "visible") load(); }, 30000);
    return () => { alive = false; window.clearInterval(id); };
  }, []);

  useEffect(() => {
    let raf = 0;
    const apply = () => { raf = 0; setScrolled(window.scrollY > 8); };
    const onScroll = () => { if (!raf) raf = window.requestAnimationFrame(apply); };
    apply();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => { window.removeEventListener("scroll", onScroll); if (raf) window.cancelAnimationFrame(raf); };
  }, []);

  useEffect(() => {
    if (content) requestAnimationFrame(() => setLoaded(true));
  }, [content]);

  useScrollReveal(reducedMotion || !!content.__fallback);

  // Highlight the menu link of the section currently in view (Plans etc.).
  useEffect(() => {
    if (!content || !("IntersectionObserver" in window)) return undefined;
    const ids = ["how", "plans", "gallery", "hyderabad"];
    const els = ids.map((id) => document.getElementById(id)).filter(Boolean);
    const io = new IntersectionObserver((entries) => {
      entries.forEach((en) => { if (en.isIntersecting) setActiveNav(en.target.id); });
    }, { rootMargin: "-35% 0px -55% 0px" });
    els.forEach((el) => io.observe(el));
    let raf = 0;
    const onTop = () => { if (raf) return; raf = window.requestAnimationFrame(() => { raf = 0; if (window.scrollY < 200) setActiveNav(""); }); };
    window.addEventListener("scroll", onTop, { passive: true });
    return () => { io.disconnect(); window.removeEventListener("scroll", onTop); if (raf) window.cancelAnimationFrame(raf); };
  }, [content]);

  // Price shown in the popup always comes from the server quote - never calculated in the browser.
  const loadCheckoutQuote = async (planId, months = checkoutMonths) => {
    setCheckoutQuoteError("");
    try {
      const q = await fetchQuote(planId, { authenticated: false, months });
      setCheckoutQuote(q);
      return q;
    } catch (e) {
      setCheckoutQuote(null);
      setCheckoutQuoteError(errText(e, "We couldn't load the latest price for this plan. Please try again."));
      return null;
    }
  };
  useEffect(() => {
    setCheckoutQuote(null);
    setCheckoutQuoteError("");
    if (checkoutPlan?.id) loadCheckoutQuote(checkoutPlan.id, checkoutMonths);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkoutPlan?.id, checkoutMonths]);
  // Links like /#plans or /#upcoming-services (from other pages) scroll to that section once the page content is shown.
  // (Hook must stay ABOVE the early return below.)
  const contentReady = !content.__fallback;
  useEffect(() => {
    const id = (location.hash || "").replace("#", "");
    if (!id || !contentReady) return undefined;
    const t = setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "auto", block: "start" }), 350);
    return () => clearTimeout(t);
  }, [location.hash, contentReady]);


  const defaultHero = {
    brand: "NAVI CARE Services",
    logo_url: "/navi-care-logo.png",
    logo_enabled: true,
    headline: "Apartment car wash, on your schedule.",
    subheadline: "Professional car cleaning delivered directly to your parking bay.",
    cta_primary: "Start subscription",
    cta_secondary: "Discover Our Services",
    image_url: "",
    images: [],
  };
  const hero = { ...defaultHero, ...(content?.hero || {}) };
  const plans = mergeLivePlans(livePlans, Array.isArray(content?.plans) ? content.plans : []);
  const contact = content?.contact || {};
  const gallery = content?.gallery || {};
  const hyderabad = content?.hyderabad || {};
  const communityCaption = String(hyderabad.subtitle || "From Gachibowli high-rises to Kondapur gated towers — we come right to your gate.").replace(/\s*Upload your own community photos from the admin console\.?/i, "").trim();
  const upcomingCities = content?.upcoming_cities || {};
  const goToPlans = () => document.getElementById("plans")?.scrollIntoView({ behavior: reducedMotion ? "auto" : "smooth", block: "start" });
  const sections = content?.sections || {};
  const offers = content?.offers || {};
  const launchEnabled = !content.__fallback && content?.launch_experience?.enabled !== false;

  // SEO: home page title / description / local business structured data.
  const seoLd = [
    { "@context": "https://schema.org", "@type": "LocalBusiness", "@id": `${SITE}/#business`, name: "NAVI CARE Services", url: `${SITE}/`,
      description: "Doorstep car wash subscriptions, AC service and home appliance repair and installation in Hyderabad.", areaServed: { "@type": "City", name: "Hyderabad" },
      address: { "@type": "PostalAddress", addressLocality: "Hyderabad", addressRegion: "Telangana", addressCountry: "IN" },
      ...(contact.phone ? { telephone: contact.phone } : {}), ...(contact.email ? { email: contact.email } : {}),
      makesOffer: [
        { "@type": "Offer", itemOffered: { "@type": "Service", name: "Doorstep car wash subscription", serviceType: "Car wash" } },
        { "@type": "Offer", itemOffered: { "@type": "Service", name: "AC service and repair", serviceType: "Air conditioner service" } },
        { "@type": "Offer", itemOffered: { "@type": "Service", name: "Home appliance repair and installation", serviceType: "Home appliance repair" } }] },
  ];

  // Phones: no details popup (the card already lists everything) - go straight to the checkout page.
  const openDetail = (plan) => { if (isPhone) openCheckout(plan, 1); else { setDetailMonths(1); setDetailPlan(plan); } };
  const openCheckout = (plan, months = 1) => {
    setDetailPlan(null);
    setHover(null);
    setCheckoutMonths(months);
    setCheckoutPlan(plan);
    setCheckoutStep("details");
    setCheckoutIntent(null);
    setCheckoutDuplicate(false);
    setCheckoutError("");
    setCheckoutShowPassword(false);
    setCheckoutForm({
      name: user?.name || "",
      phone: user?.phone || "",
      email: user?.email || "",
      pin: "",
      confirmPin: "",
      vehicleNumber: "",
      ppf: "",
      referralCode: authRef || "",
    });
    setKnownVehicles(null);
    if (user) {
      api.get("/vehicles", { timeout: 30000 }).then((r) => {
        const list = Array.isArray(r.data) ? r.data : [];
        setKnownVehicles(list);
        const plate = String(list[0]?.plate || "").toUpperCase();
        const known = typeof list[0]?.ppf_coated === "boolean" ? boolToPpf(list[0].ppf_coated) : "";
        setCheckoutForm((f) => ({ ...f, vehicleNumber: f.vehicleNumber || plate, ppf: f.ppf || known }));
      }).catch(() => setKnownVehicles([]));
    }
  };

  // Never ask a signed-in customer for something we already hold. A field is hidden only when the saved value is valid.
  const checkoutKnown = (key) => {
    if (!user) return false;
    if (key === "name") return String(user.name || "").trim().length >= 2;
    if (key === "email") return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(user.email || ""));
    if (key === "phone") {
      let p = String(user.phone || "").replace(/\D/g, "");
      if (p.length === 12 && p.startsWith("91")) p = p.slice(2); else if (p.length === 11 && p.startsWith("0")) p = p.slice(1);
      return /^[6-9]\d{9}$/.test(p);
    }
    if (key === "vehicleNumber") return Array.isArray(knownVehicles) && knownVehicles.length === 1;
    return false;
  };

  const planOptions = (id) => planTerms[id] || [];
  const onCardEnter = (plan, el) => {
    // hover popup is for mouse users only; touch devices use "View details"
    if (!window.matchMedia("(hover: hover) and (pointer: fine)").matches) return;
    window.clearTimeout(hoverTimer.current);
    hoverTimer.current = window.setTimeout(() => {
      if (planOptions(plan.id).length < 2) return;
      setHover({ plan, rect: el.getBoundingClientRect(), months: 12 });
    }, 140);
  };
  const onCardLeave = () => window.clearTimeout(hoverTimer.current);

  const checkoutContinue = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    if (!checkoutPlan || checkoutLoading) return;
    if (!checkoutQuote) {
      const fresh = await loadCheckoutQuote(checkoutPlan.id);
      if (!fresh) { setCheckoutError("We couldn't load the latest price for this plan. Please try again."); return; }
    }

    const fail = (msg) => { if (/Terms were updated/i.test(String(msg))) terms.reload(); setCheckoutError(msg); toast.error(msg); };
    const name = checkoutForm.name.trim();
    // Accept +91 / 91 / 0 prefixes (browser autofill often adds +91) and keep the last 10 digits.
    let phone = checkoutForm.phone.replace(/\D/g, "");
    if (phone.length === 12 && phone.startsWith("91")) phone = phone.slice(2);
    else if (phone.length === 11 && phone.startsWith("0")) phone = phone.slice(1);
    const email = checkoutForm.email.trim().toLowerCase();
    let plate = checkoutForm.vehicleNumber.replace(/[\s-]+/g, "").toUpperCase();
    const savedVehicle = user && Array.isArray(knownVehicles) && knownVehicles.length === 1 ? knownVehicles[0] : null;
    if (savedVehicle) plate = String(savedVehicle.plate || "").replace(/[\s-]+/g, "").toUpperCase();

    setCheckoutError("");
    if (user && knownVehicles === null) return fail("Loading your saved details. Please try again in a moment.");
    if (name.length < 2) return fail("Enter your full name");
    if (!/^[6-9]\d{9}$/.test(phone)) return fail("Enter a valid 10-digit Indian mobile number");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return fail("Enter a valid email address");
    if (/\.con$/.test(email)) return fail("Your email ends with \".con\" - did you mean \".com\"?");
    if (!user && !/^\d{4}$/.test(checkoutForm.pin)) return fail("Create a 4-digit PIN (numbers only)");
    if (!user && checkoutForm.pin !== checkoutForm.confirmPin) return fail("PINs do not match");
    if (!user && /^(\d)\1{3}$|^(0123|1234|2345|3456|4567|5678|6789|3210|4321|5432|6543|7654|8765|9876|2580|1212|2020)$/.test(checkoutForm.pin)) return fail("That PIN is too easy to guess. Pick 4 digits that are not repeated or in a row.");
    if (!checkoutForm.ppf) return fail("Please tell us whether your vehicle is already covered with PPF coating");
    const ppfCoated = ppfToBool(checkoutForm.ppf);
    if (!savedVehicle && !/^[A-Z]{2}\d{1,2}[A-Z]{1,3}\d{1,4}$/.test(plate)) return fail("Enter a valid vehicle registration number, e.g. TS09AB1234");

    // Advance the UI immediately after validation. Previously the modal waited
    // for the API call before changing steps, which made a slow/failed request
    // look like the button was not working at all.
    setCheckoutIntent(null);
    setCheckoutStep("payment");
    setCheckoutLoading(true);

    try {
      let data;

      if (user) {
        const { data: vehicles } = await api.get("/vehicles", { timeout: 30000 });
        let existingVehicle = (Array.isArray(vehicles) ? vehicles : []).find(
          (v) => String(v?.plate || "").replace(/\s+/g, "").toUpperCase() === plate
        ) || (Array.isArray(vehicles) ? vehicles[0] : null);
        if (!existingVehicle?.vehicle_id) {
          // First vehicle: registration number + PPF answer are all we need here (brand / model are confirmed in the dashboard).
          existingVehicle = (await api.post("/vehicles", { plate, ppf_coated: ppfCoated, quick: true }, { timeout: 30000 })).data;
        } else if (existingVehicle.ppf_coated !== ppfCoated) {
          await api.put(`/vehicles/${existingVehicle.vehicle_id}/ppf`, { ppf_coated: ppfCoated }, { timeout: 30000 });
        }
        const intentResponse = await api.post("/subscription/intent", {
          plan_id: checkoutPlan.id,
          mode: "new",
          vehicle_id: existingVehicle.vehicle_id,
          reference: "",
          months: checkoutMonths,
          referral_code: checkoutForm.referralCode.trim().toUpperCase(),
          expected_price: checkoutQuote?.subtotal,
          ...terms.payload,
        }, { timeout: 30000 });
        data = { intent: intentResponse.data };
      } else {
        try {
          const response = await api.post("/checkout/start", {
            plan_id: checkoutPlan.id,
            name,
            phone,
            email,
            pin: checkoutForm.pin,
            vehicle_number: plate,
            ppf_coated: ppfCoated,
            months: checkoutMonths,
            referral_code: checkoutForm.referralCode.trim().toUpperCase(),
            expected_price: checkoutQuote?.subtotal,
            ...terms.payload,
          }, { timeout: 30000 });
          data = response.data;
        } catch (startError) {
          // Recover a customer created by an earlier checkout attempt.
          if (startError?.response?.status !== 409) throw startError;
          let loginResponse;
          try {
            loginResponse = await api.post("/auth/login-pin", { identifier: phone, pin: checkoutForm.pin }, { timeout: 30000 });
          } catch (loginErr) {
            // Account exists but the PIN differs: ask the customer to log in instead.
            setCheckoutStep("details");
            setCheckoutDuplicate(true);
            setCheckoutLoading(false);
            const dupMsg = "An account already exists with these details. Please log in to continue.";
            setCheckoutError(dupMsg);
            toast.error(dupMsg);
            return;
          }
          const loggedUser = loginResponse.data?.user;
          if (!loginResponse.data?.token || !loggedUser?.user_id) throw startError;

          localStorage.setItem("nw_token", loginResponse.data.token);
          localStorage.setItem("nw_user", JSON.stringify(loggedUser));
          if (setUser) setUser(loggedUser);

          const { data: vehicles } = await api.get("/vehicles", { timeout: 30000 });
          const vehicleList = Array.isArray(vehicles) ? vehicles : [];
          const existingVehicle = vehicleList.find(
            (v) => String(v?.plate || "").replace(/\s+/g, "").toUpperCase() === plate
          ) || vehicleList[0];
          if (!existingVehicle?.vehicle_id) {
            throw new Error("Your account was found, but no vehicle is available. Please add your vehicle and try again.");
          }
          if (existingVehicle.ppf_coated !== ppfCoated) await api.put(`/vehicles/${existingVehicle.vehicle_id}/ppf`, { ppf_coated: ppfCoated }, { timeout: 30000 });
          const intentResponse = await api.post("/subscription/intent", {
            plan_id: checkoutPlan.id,
            mode: "new",
            vehicle_id: existingVehicle.vehicle_id,
            reference: "",
            months: checkoutMonths,
            referral_code: checkoutForm.referralCode.trim().toUpperCase(),
            expected_price: checkoutQuote?.subtotal,
            ...terms.payload,
          }, { timeout: 30000 });
          data = { intent: intentResponse.data, token: loginResponse.data.token, user: loggedUser };
        }

        if (data?.token) localStorage.setItem("nw_token", data.token);
        if (data?.user) localStorage.setItem("nw_user", JSON.stringify(data.user));
      }

      if (!data?.intent?.intent_id) {
        throw new Error("Payment order could not be created. Please try again.");
      }
      setCheckoutIntent(data.intent);
      if (!user && setUser && data.user) setUser(data.user);
      await payWithCashfree({ kind: "subscription", id: data.intent.intent_id });
      return;
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 412) {
        // Price changed while the popup was open: show the new amount and make the customer re-confirm.
        await loadCheckoutQuote(checkoutPlan.id);
        setCheckoutStep("details");
      }
      // Stay on the payment step and show a retry action. Never silently return
      // the customer to the details form after they have passed validation.
      setCheckoutIntent(null);
      const msg = errText(e, "Could not prepare payment. Please retry.");
      setCheckoutError(msg);
      toast.error(msg);
    } finally {
      setCheckoutLoading(false);
    }
  };

  const copyCheckoutValue = async (value, label) => {
    try { await navigator.clipboard.writeText(String(value || "")); toast.success(`${label} copied`); } catch {}
  };

  return (
    <>
      <Seo
        title="NAVI CARE Services | Doorstep Car Wash, AC Service & Appliance Repair and Home Care in Hyderabad"
        description="NAVI CARE Services: doorstep car wash plans, AC service, home appliance repair & installation and home care in Hyderabad. Book online, pay securely and track every visit with NAVI CARE Services."
        keywords="doorstep car wash Hyderabad, car wash subscription Hyderabad, AC service Hyderabad, AC repair at home Hyderabad, home appliance repair Hyderabad, washing machine repair Hyderabad, refrigerator repair Hyderabad, appliance installation Hyderabad, apartment car cleaning, car wash near me, NAVI CARE"
        path="/" jsonLd={seoLd}
      />
      {launchEnabled && <LaunchExperience content={content} />}
    <div className="min-h-screen bg-background text-foreground antialiased navi-landing-shell" data-testid="landing-root" style={offers.published && offers.text ? undefined : { "--navi-promo-h": "0px" }}>
      {/* Fixed chrome lives at the page root (outside the hero stacking context) so no section can ever paint over it. */}
        <header className={`navi-hero-nav ${scrolled ? "is-scrolled" : ""}`}>
          <div className="w-full max-w-none px-3 sm:px-8 lg:px-12 xl:px-16 h-16 flex flex-nowrap items-center justify-between gap-2 xl:gap-4" data-testid="header-bar">
            <button
              onClick={() => window.scrollTo({ top: 0, behavior: reducedMotion ? "auto" : "smooth" })}
              className="flex items-center gap-2 tracking-tight text-white py-2 min-w-0 xl:shrink-0 navi-nav-brand"
              aria-label="Back to top"
            >
              <BrandName services shine mark={false} style={{ fontSize: Math.max(hero.brand_font_size || 15, 32) }} />
            </button>
            <div className="flex items-center gap-3 sm:gap-5 shrink-0">
              <button
                type="button"
                data-testid="header-sign-in"
                className="navi-signin-cta"
                onClick={() => user ? navigate("/dashboard") : openAuth("signin")}
                aria-label={user ? "Open customer dashboard" : "Sign in to your NAVI CARE account"}
                title={user ? "Open your dashboard" : "Sign in to your account"}
              >
                {user ? "DASHBOARD" : "LOGIN"}
              </button>
              <div className="navi-book" ref={bookRef}>
                <button type="button" className={`navi-book-btn${bookOpen ? " is-open" : ""}`} data-testid="nav-book-now" aria-haspopup="menu" aria-expanded={bookOpen} onClick={() => setBookOpen((v) => !v)}>
                  <span className="navi-book-label">Book Now</span>
                  <svg className="navi-book-caret" viewBox="0 0 24 24" aria-hidden="true"><path d="M6 9l6 6 6-6" /></svg>
                </button>
                <div className={`navi-book-menu${bookOpen ? " is-open" : ""}`} role="menu" aria-hidden={!bookOpen}>
                  <p className="navi-book-menu-head">Choose a service</p>
                  <button type="button" role="menuitem" tabIndex={bookOpen ? 0 : -1} data-testid="book-car-care" onClick={() => goBook("/car-wash-hyderabad")}><span className="navi-bm-ico"><Car aria-hidden="true" /></span><span className="navi-bm-txt"><b>Car Care</b><small>Doorstep wash plans</small></span><ChevronRight className="navi-bm-go" aria-hidden="true" /></button>
                  <button type="button" role="menuitem" tabIndex={bookOpen ? 0 : -1} data-testid="book-appliance-repair" onClick={() => goBook("/home-appliance-repair-hyderabad")}><span className="navi-bm-ico"><Wrench aria-hidden="true" /></span><span className="navi-bm-txt"><b>Appliance Repair</b><small>AC, washer, fridge &amp; more</small></span><ChevronRight className="navi-bm-go" aria-hidden="true" /></button>
                  <button type="button" role="menuitem" tabIndex={bookOpen ? 0 : -1} data-testid="book-sofa-repair" onClick={() => goBook("/sofa-repair-hyderabad")}><span className="navi-bm-ico"><Armchair aria-hidden="true" /></span><span className="navi-bm-txt"><b>Sofa Repair</b><small>Repair &amp; deep cleaning</small></span><ChevronRight className="navi-bm-go" aria-hidden="true" /></button>
                </div>
              </div>
            </div>
          </div>
        </header>
        {/* Content softly fades out just before it reaches the fixed header / offer bar. */}
        <div
          className={`navi-top-fade${scrolled ? "" : " is-off"}`}
          aria-hidden="true"
          style={{ top: offers.published && offers.text ? "calc(var(--navi-header-h) + var(--navi-promo-h))" : "var(--navi-header-h)" }}
        />

        {offers.published && offers.text && (
          <div
            className={`navi-offer-ticker navi-offer-fixed${scrolled ? "" : " is-over-hero"}`}
            data-testid="offer-ticker"
            style={{ "--navi-offer-accent": offers.background || "transparent", color: offers.text_color || "#FFFFFF" }}
          >
            <div className="navi-offer-track" style={{ "--offer-duration": `${Math.max(8, Number(offers.speed_seconds) || 18) * 3}s` }}>
              {/* 12 copies (two identical halves of 6): a short offer now fills the whole bar instead of leaving it looking empty. */}
              {Array.from({ length: 12 }, (_, copy) => copy).map((copy) => (
                <div key={copy} className="navi-offer-item" style={{ fontSize: `${Math.max(10, Number(offers.font_size) || 13)}px`, fontFamily: offers.font_style === "display" ? "var(--font-display, inherit)" : offers.font_style === "serif" ? "Georgia, serif" : offers.font_style === "mono" ? "ui-monospace, SFMono-Regular, Menlo, monospace" : "inherit" }}>
                  {offers.text}
                </div>
              ))}
            </div>
          </div>
        )}


      <main id="main-content">
      {/* Animated service heroes: Car Care, Home Care, Appliance Repair (words in lib/serviceHeroes.js). They replace the old car-wash video hero.
          Car -> shows the car plans.  Home / Appliance -> shows the home services and hides the car plans. */}
      <ServiceHeroes navigate={navigate} reducedMotion={reducedMotion} onSelect={chooseService} />

      {/* Pricing */}
      <section id="plans" data-testid="car-plans-section" hidden={serviceFocus === "home"} style={serviceFocus === "home" ? { display: "none" } : undefined} className="px-4 sm:px-6 lg:px-10 2xl:px-16 py-10 sm:py-14 navi-section-reveal">
        <div className="navi-wrap navi-plans-stage">
          <div className="navi-plans-head">
            <span className="navi-plans-eyebrow">Monthly plans</span>
            <h2>{sections.plans_title || "Plans for every driveway"}</h2>
            <p className="navi-plans-lead">Choose a monthly subscription and keep your car clean without the daily hassle.</p>
            <p className="navi-plans-sub">{sections.plans_subtitle || "Prices shown before GST. Change or cancel anytime from your dashboard."}</p>
          </div>
          <div className="mt-8">
            <PlanShowcase plans={plans} planOptions={planOptions} tone="light" compact compare
              onChoose={(p) => openCheckout(p, 1)}
              />
          </div>
          <StatsStrip stats={content?.stats} reduced={reducedMotion} />
        </div>
      </section>

      <Suspense fallback={null}><GoogleReviews /></Suspense>

      {/* Upcoming home services appear below the plan overview. */}
      <UpcomingServices pick={servicePick} carPlansHidden={serviceFocus === "home"} onShowCarPlans={() => { chooseService("car"); setTimeout(() => document.getElementById("plans")?.scrollIntoView({ behavior: "smooth", block: "start" }), 80); }} />

      {false && hover && !detailPlan && !checkoutPlan && (
        <PlanHoverPopup
          plan={hover.plan}
          rect={hover.rect}
          options={planOptions(hover.plan.id)}
          months={hover.months}
          onMonths={(m) => setHover((h) => (h ? { ...h, months: m } : h))}
          onChoose={() => openCheckout(hover.plan, hover.months)}
          onClose={() => setHover(null)}
        />
      )}

      <SmoothModal open={!!detailPlan} onClose={() => setDetailPlan(null)} z={80} maxWidth="sm:max-w-2xl" testId="plan-detail-popup">
        {detailPlan && (
            <div className="p-5 sm:p-7">
              <div className="navi-rise flex items-start justify-between gap-4" style={{ "--i": 0 }}>
                <div className="min-w-0"><PlanTag plan={detailPlan} /><h3 className="mt-2 text-xl sm:text-2xl font-semibold tracking-tight">{detailPlan.name}</h3></div>
                <button onClick={() => setDetailPlan(null)} aria-label="Close" className="w-9 min-h-9 shrink-0 rounded-full border border-border text-muted-foreground hover:bg-accent">×</button>
              </div>
              <div className="navi-rise" style={{ "--i": 1 }}><PlanPrice plan={detailPlan} interval={detailPlan.interval} size="md" className="mt-4" /></div>
              {planOptions(detailPlan.id).length > 1 && (
                <div className="navi-rise mt-5" style={{ "--i": 2 }}>
                  <div className="mb-2 text-[0.6875rem] uppercase tracking-[.16em] font-semibold text-muted-foreground">Choose your duration</div>
                  <TermPicker options={planOptions(detailPlan.id)} value={detailMonths} onChange={setDetailMonths} interval={detailPlan.interval || "month"} compact />
                </div>
              )}
              <details className="navi-rise group mt-5 rounded-2xl border border-border/60 bg-card/60 p-3.5" style={{ "--i": 3 }}>
                <summary className="flex cursor-pointer list-none items-center justify-between text-sm font-semibold">
                  <span>Plan details</span><span className="text-xs font-semibold text-primary group-open:hidden">View all</span><span className="hidden text-xs font-semibold text-primary group-open:inline">Hide</span>
                </summary>
                <div className="mt-3 grid gap-x-5 gap-y-2 sm:grid-cols-2">{(Array.isArray(detailPlan.features) ? detailPlan.features : []).map((f, i) => <div key={i} className="flex gap-2 text-sm leading-snug"><Check className="mt-0.5 w-4 h-4 shrink-0 text-primary" /><span>{f}</span></div>)}</div>
              </details>
              <button onClick={() => openCheckout(detailPlan, detailMonths)} className="navi-rise mt-5 w-full min-h-12 rounded-full bg-foreground text-background font-semibold" style={{ "--i": 4 }}>Choose {detailPlan.name}{detailMonths > 1 ? ` · ${detailMonths} months` : ""}</button>
            </div>
        )}
      </SmoothModal>

      <SmoothModal open={!!checkoutPlan} onClose={() => setCheckoutPlan(null)} z={100} maxWidth="sm:max-w-xl" panelClassName="navi-modal-full" testId="checkout-popup">
        {checkoutPlan && (<>
            <div className="sticky top-0 z-10 flex items-center justify-between gap-4 border-b border-border bg-background/95 backdrop-blur-xl px-5 py-4 sm:px-7">
              <div>
                <h2 className="text-xl sm:text-2xl font-semibold tracking-tight">
                  {checkoutStep === "details" ? "Complete your subscription" : checkoutStep === "payment" ? "Complete your payment" : "Processing payment"}
                </h2>
              </div>
              <button type="button" onClick={() => setCheckoutPlan(null)} aria-label="Close checkout" className="h-9 w-9 shrink-0 rounded-full border border-border bg-card text-foreground inline-flex items-center justify-center hover:bg-accent transition-colors"><X className="h-4 w-4" /></button>
            </div>

            <div className="px-5 py-5 sm:px-7 sm:py-7">
              <div className="grid grid-cols-3 gap-1.5 mb-6">
                {[["1","Plan"],["2","Details"],["3","Payment"]].map(([n,label],i) => {
                  const active = (checkoutStep === "details" && i <= 1) || (checkoutStep === "payment" && i <= 2) || checkoutStep === "submitted";
                  return <div key={n} className={`rounded-full px-2 py-2 text-center text-[0.65rem] sm:text-xs font-semibold border ${active ? "border-primary/30 bg-primary/10 text-primary" : "border-border text-muted-foreground"}`}>{n}. {label}</div>;
                })}
              </div>

              <div className="rounded-2xl border border-border bg-card p-4 sm:p-5">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="text-[0.625rem] uppercase tracking-[.18em] text-muted-foreground font-semibold">Selected Plan</div>
                    <div className="mt-1.5 text-lg font-semibold">{checkoutPlan.name}</div>
                    <div className="mt-1 text-sm text-muted-foreground">{(Array.isArray(checkoutPlan.features) ? checkoutPlan.features : []).slice(0,3).join(" · ")}</div>
                  </div>
                  <div className="text-right shrink-0">
                    {checkoutQuote ? <><div className="text-2xl font-semibold" data-testid="checkout-plan-price">{inr(checkoutQuote.subtotal)}</div>
                    <div className="text-xs text-muted-foreground">{checkoutMonths > 1 ? `for ${checkoutMonths} months` : `/${checkoutPlan.interval || "plan"}`}</div></> : <div className="text-sm text-muted-foreground">{checkoutQuoteError ? "Price unavailable" : "Loading price…"}</div>}
                    {checkoutStep === "details" && <button type="button" onClick={() => { setCheckoutPlan(null); setTimeout(() => document.getElementById("plans")?.scrollIntoView({ behavior: reducedMotion ? "auto" : "smooth", block: "start" }), 0); }} className="mt-2 text-xs font-semibold text-primary hover:underline">Change plan</button>}
                  </div>
                </div>
              </div>

              {checkoutStep === "details" && checkoutQuoteError && (
                <div role="alert" className="mt-4 rounded-2xl border border-destructive/30 bg-destructive/5 p-4 text-sm">
                  <div className="font-medium text-destructive">{checkoutQuoteError}</div>
                  <button type="button" onClick={() => loadCheckoutQuote(checkoutPlan.id)} className="mt-2 text-xs font-semibold text-primary hover:underline">Retry</button>
                </div>
              )}
              {checkoutStep === "details" && planOptions(checkoutPlan.id).length > 1 && (
                <div className="mt-4">
                  <div className="mb-2 text-[0.6875rem] uppercase tracking-[.16em] font-semibold text-muted-foreground">Plan duration</div>
                  <TermPicker options={planOptions(checkoutPlan.id)} value={checkoutMonths} onChange={setCheckoutMonths} interval={checkoutPlan.interval || "month"} compact />
                </div>
              )}
              {checkoutStep === "details" && checkoutQuote && <PriceBreakdown quote={checkoutQuote} gst={checkoutGst} className="mt-4" />}

              {checkoutStep === "details" && (
                <form onSubmit={checkoutContinue} className="mt-5 space-y-4">
                  {user && ["name","phone","email","vehicleNumber"].some(checkoutKnown) && (
                    <div data-testid="checkout-saved-details" className="rounded-xl border border-border bg-muted/40 p-3 text-sm">
                      <div className="font-medium">Using your saved details</div>
                      <div className="mt-0.5 text-muted-foreground">
                        {[checkoutKnown("name") && user.name, checkoutKnown("phone") && user.phone, checkoutKnown("email") && user.email, checkoutKnown("vehicleNumber") && knownVehicles[0]?.plate].filter(Boolean).join(" · ")}
                      </div>
                    </div>
                  )}
                  <div className="grid sm:grid-cols-2 gap-4">
                    {[
                      ["name","Customer Name","Enter your full name",User,"text"],
                      ["phone","Contact Number","10-digit mobile number",Phone,"tel"],
                      ["email","Email ID","Enter your email address",Mail,"email"],
                      ["vehicleNumber","Vehicle Number","e.g. TS09AB1234",Car,"text"],
                    ].filter(([key]) => !checkoutKnown(key)).map(([key,label,placeholder,Icon,type]) => (
                      <label key={key} className="block">
                        <span className="text-sm font-medium">{label} <span className="text-destructive">*</span></span>
                        <div className="mt-1.5 relative">
                          <Icon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                          <input
                            value={checkoutForm[key]}
                            onChange={(e) => { setCheckoutError(""); setCheckoutForm({...checkoutForm,[key]: key === "vehicleNumber" ? e.target.value.toUpperCase() : e.target.value}); }}
                            type={type}
                            placeholder={placeholder}
                            required
                            autoComplete={key === "email" ? "email" : key === "phone" ? "tel" : key === "name" ? "name" : "off"}
                            inputMode={key === "phone" ? "tel" : key === "email" ? "email" : undefined}
                            className="w-full min-h-11 rounded-xl border border-input bg-background pl-10 pr-3 text-foreground placeholder:text-muted-foreground outline-none focus:ring-2 focus:ring-ring/30 focus:border-ring"
                          />
                        </div>
                      </label>
                    ))}
                  </div>
                  {!user && (
                    <div className="grid sm:grid-cols-2 gap-4">
                      {[
                        ["pin","Create 4-digit PIN"],
                        ["confirmPin","Confirm PIN"],
                      ].map(([key,label]) => (
                        <label key={key} className="block">
                          <span className="text-sm font-medium">{label} <span className="text-destructive">*</span></span>
                          <div className="mt-1.5 relative">
                            <LockKeyhole className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                            <input
                              data-testid={`checkout-${key}`}
                              value={checkoutForm[key]}
                              onChange={(e) => { setCheckoutError(""); setCheckoutForm({...checkoutForm,[key]:e.target.value.replace(/\D/g, "").slice(0, 4)}); }}
                              type={checkoutShowPassword ? "text" : "password"}
                              inputMode="numeric"
                              pattern="\d{4}"
                              maxLength={4}
                              placeholder="••••"
                              required
                              autoComplete="off"
                              className="w-full min-h-11 rounded-xl border border-input bg-background pl-10 pr-11 tracking-[0.5em] text-foreground placeholder:text-muted-foreground outline-none focus:ring-2 focus:ring-ring/30 focus:border-ring"
                            />
                            <button type="button" onClick={() => setCheckoutShowPassword(v => !v)} aria-label={checkoutShowPassword ? "Hide PIN" : "Show PIN"} className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8 rounded-lg text-muted-foreground hover:text-foreground inline-flex items-center justify-center">
                              {checkoutShowPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                            </button>
                          </div>
                        </label>
                      ))}
                      <p className="sm:col-span-2 -mt-2 text-xs text-muted-foreground">You will log in with your mobile number and this PIN.</p>
                    </div>
                  )}
                  <PpfQuestion value={checkoutForm.ppf} onChange={(ppf) => { setCheckoutError(""); setCheckoutForm({ ...checkoutForm, ppf }); }} testId="checkout-ppf" />
                  <label className="block">
                    <span className="text-sm font-medium">Referral code <span className="text-muted-foreground font-normal">(optional)</span></span>
                    <div className="mt-1.5 relative">
                      <Copy className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                      <input
                        data-testid="checkout-referral-code"
                        value={checkoutForm.referralCode}
                        onChange={(e) => setCheckoutForm({ ...checkoutForm, referralCode: e.target.value.toUpperCase().replace(/\s+/g, "") })}
                        placeholder="Friend's referral code"
                        autoComplete="off"
                        className="w-full min-h-11 rounded-xl border border-input bg-background pl-10 pr-3 font-mono uppercase text-foreground placeholder:font-sans placeholder:normal-case placeholder:text-muted-foreground outline-none focus:ring-2 focus:ring-ring/30 focus:border-ring"
                      />
                    </div>
                  </label>
                  {checkoutDuplicate && (
                    <div className="rounded-2xl border border-amber-500/25 bg-amber-500/5 p-4">
                      <div className="font-semibold text-sm">Account already exists</div>
                      <p className="mt-1 text-xs text-muted-foreground">Please sign in to continue with this plan, or use different customer details.</p>
                      <div className="mt-3 flex gap-2">
                        <button type="button" onClick={() => openAuth("signin")} className="min-h-10 flex-1 rounded-xl bg-primary text-primary-foreground text-sm font-semibold">Login & Continue</button>
                        <button type="button" onClick={() => setCheckoutDuplicate(false)} className="min-h-10 px-4 rounded-xl border border-border text-sm font-medium hover:bg-accent">Use another</button>
                      </div>
                    </div>
                  )}
                  {checkoutError && (
                    <div role="alert" className="rounded-xl border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm font-medium text-destructive">{checkoutError}</div>
                  )}
                  <p className="text-xs text-muted-foreground">Your plan is already selected. You will not need to choose it again.</p>
                  <TermsAcceptance terms={terms} />
                  <button type="button" onClick={() => checkoutContinue()} disabled={checkoutLoading || !checkoutQuote || !checkoutGst.bill || !terms.ready} className="w-full min-h-12 rounded-xl bg-primary text-primary-foreground font-semibold inline-flex items-center justify-center gap-2 hover:bg-primary/90 disabled:opacity-60 disabled:cursor-not-allowed">
                    {checkoutLoading ? "Preparing secure checkout…" : `Continue to Payment${checkoutGst.bill ? ` · ${inr(checkoutGst.bill.total)}` : ""}`} <ArrowUpRight className="h-4 w-4" />
                  </button>
                  <p data-testid="checkout-policy-note" className="text-center text-[0.6875rem] leading-relaxed text-muted-foreground">
                    By continuing you agree to our <Link to="/policy/terms-and-conditions" target="_blank" className="underline underline-offset-2">Terms</Link>, <Link to="/policy/payment-policy" target="_blank" className="underline underline-offset-2">Payment Policy</Link> and <Link to="/policy/refund-cancellation-policy" target="_blank" className="underline underline-offset-2">Refund Policy</Link>. If payment fails, your booking is held for 20 minutes so you can retry.
                  </p>
                  {user && <p className="text-center text-xs text-muted-foreground">You are signed in. Your existing account details will be used on the subscription page.</p>}
                </form>
              )}

              {checkoutStep === "payment" && (
                <div className="mt-5 space-y-4">
                  {!checkoutIntent ? (
                    <div className="rounded-2xl border border-primary/20 bg-primary/[0.05] p-5 text-center">
                      <BrandLoader fullscreen={false} className="!min-h-0 !p-0" />
                      <div className="mt-3 font-semibold">{checkoutLoading ? "Preparing your payment…" : "Secure payment setup needs one more try"}</div>
                      <p className="mt-1 text-sm text-muted-foreground">{checkoutLoading ? "Please wait while we create your secure payment request." : "Your details are saved. Tap below to create the payment request."}</p>
                      {!checkoutLoading && checkoutError && <p role="alert" className="mt-3 text-sm font-medium text-destructive">{checkoutError}</p>}
                      {!checkoutLoading && (
                        <button type="button" onClick={() => checkoutContinue()} className="mt-4 min-h-11 px-5 rounded-xl bg-primary text-primary-foreground font-semibold">Retry Payment Setup</button>
                      )}
                    </div>
                  ) : null}
                  {checkoutIntent && (
                    <div className="rounded-2xl border border-primary/25 bg-primary/[0.045] p-5">
                      <div className="flex items-center gap-2 font-semibold"><ShieldCheck className="h-4 w-4 text-primary" /> Secure online payment</div>
                      <div className="mt-2 text-sm text-muted-foreground">Amount to pay: <b className="text-foreground">{inr(checkoutIntent?.amount)}</b></div>
                      <PriceBreakdown quote={intentToQuote(checkoutIntent)} promoDiscount={checkoutIntent?.discount_amount} promoCode={checkoutIntent?.promo_code} gst={{ bill: billFromOrder(checkoutIntent) }} className="mt-3" />
                      <button type="button" disabled={checkoutLoading || !cashfreeOn} onClick={async()=>{try{setCheckoutLoading(true);await payWithCashfree({kind:"subscription",id:checkoutIntent.intent_id})}catch(e){toast.error(e?.response?.data?.detail||e?.message||"Could not open Cashfree checkout")}finally{setCheckoutLoading(false)}}} className="mt-5 w-full min-h-12 rounded-xl bg-primary text-primary-foreground font-semibold disabled:opacity-60">
                        {checkoutLoading ? "Opening secure checkout…" : `PAY ONLINE · ${inr(checkoutIntent?.amount)}`}
                      </button>
                      <p className="mt-3 text-center text-xs text-muted-foreground">Cashfree securely processes UPI, cards and netbanking. Your Navi Pass activates automatically after confirmed payment.</p>
                    </div>
                  )}                </div>
              )}

              {checkoutStep === "submitted" && (
                <div className="mt-7 text-center">
                  <div className="mx-auto h-14 w-14 rounded-full bg-primary/10 text-primary flex items-center justify-center"><ShieldCheck className="h-7 w-7" /></div>
                  <h3 className="mt-4 text-xl font-semibold">Payment Processing</h3>
                  <p className="mt-2 text-sm text-muted-foreground">Your Cashfree payment is being confirmed. Your subscription and Navi Pass will activate automatically after successful payment.</p>
                  <button type="button" onClick={() => navigate("/dashboard")} className="mt-6 w-full min-h-12 rounded-xl bg-primary text-primary-foreground font-semibold">Go to Dashboard</button>
                </div>
              )}
            </div>
        </>)}
      </SmoothModal>

      {/* Gallery — light (day theme) section */}
      {gallery?.images?.length > 0 && (
        <section id="gallery" data-testid="gallery-section" className="navi-gallery-day px-6 py-12 sm:py-16 navi-section-reveal">
          <div className="navi-wrap">
            <div className="max-w-[35rem]">
              <h2 className="text-3xl sm:text-[2.5rem] font-semibold tracking-tight leading-tight">{gallery.title}</h2>
              {gallery.subtitle && <p className="mt-4 text-lg leading-relaxed text-muted-foreground">{gallery.subtitle}</p>}
            </div>
            {(() => {
              const imgs = gallery.images.filter(Boolean);
              const names = ["Car Wash", "Tyre Polish", "Interior Clean", "Detail Care"];
              const set = Array.from({ length: Math.max(1, Math.ceil(6 / imgs.length)) }, () => imgs).flat();
              return (
                <div className="navi-service-marquee mt-8" data-testid="service-marquee" style={{ "--marquee-duration": `${Math.max(24, set.length * 5)}s` }}>
                  <div className="navi-service-track">
                    {[0, 1].map((copy) => (
                      <div key={copy} className="navi-service-set" aria-hidden={copy === 1 ? "true" : undefined}>
                        {set.map((url, i) => (
                          <div key={`${copy}-${i}`} data-testid={copy === 0 ? `gallery-thumb-${i}` : undefined} className="navi-service-card is-link" {...(copy === 0 ? { role: "link", tabIndex: 0, "aria-label": `${names[i % 4]} — view plans`, onClick: goToPlans, onKeyDown: (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); goToPlans(); } } } : { onClick: goToPlans })}>
                            <div className="navi-service-image-wrap">
                              <img src={resolveMediaUrl(url)} alt={copy === 0 ? names[i % 4] : ""} className="navi-service-image" style={mediaStyle(url)} loading="lazy"
                                onError={(e) => { e.currentTarget.closest(".navi-service-card").style.display = "none"; }} />
                              <div className="navi-service-glass" />
                            </div>
                            <div className="navi-service-meta"><span>{String((i % imgs.length) + 1).padStart(2, "0")}</span><strong>{names[i % 4]}</strong></div>
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}
          </div>
        </section>
      )}

      {/* Hyderabad communities: a compact, static information strip. */}
      {(hyderabad?.images || []).filter(Boolean).length > 0 && (
        <section id="hyderabad" data-testid="hyderabad-section" aria-labelledby="hyderabad-title" className="navi-community-small-ad px-6 py-4 sm:py-5">
          <div className="navi-community-small-card navi-wrap grid items-center gap-4 md:grid-cols-[minmax(0,1fr)_auto]">
            <div className="min-w-0">
              <h2 id="hyderabad-title" className="text-lg sm:text-xl font-semibold tracking-tight leading-snug">{hyderabad.title || "Serving Hyderabad's finest apartment communities."}</h2>
              {communityCaption && <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{communityCaption}</p>}
            </div>
            <div className="navi-community-thumbnails flex flex-wrap gap-2" aria-label="Communities we serve">
              {hyderabad.images.filter(Boolean).map((url, i) => (
                <div key={i} data-testid={`hyderabad-img-${i}`} className="navi-community-card relative w-24 sm:w-28 aspect-[16/9] rounded-lg overflow-hidden">
                  <img src={resolveMediaUrl(url)} alt={`Apartment community near ${["Gachibowli", "Kondapur", "Financial District", "Madhapur", "Hitec City", "Jubilee Hills"][i % 6]}`} className="w-full h-full object-cover" style={mediaStyle(url)} loading="lazy" />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      <section id="refer-earn" className="navi-refer-section navi-section-reveal">
        <div className="navi-refer-card navi-refer-card--compact navi-wrap" data-testid="landing-referral-card">
          <div className="navi-refer-mark"><ReferAnimation logoUrl={hero.logo_url} /></div>
          <div className="navi-refer-copy">
            <span className="navi-refer-kicker">A LITTLE CARE GOES A LONG WAY</span>
            <h2>Share the shine. Earn {refRate}%.</h2>
            <p>Invite a friend to NAVI CARE. Earn <mark className="navi-refer-hl">{refRate}% of their plan amount</mark> after their <mark className="navi-refer-hl is-soft">first payment is approved</mark>.</p>
            <ol className="navi-refer-steps" data-testid="landing-referral-steps">
              <li><span>01</span> Get your code</li>
              <li><span>02</span> Share with a friend</li>
              <li><span>03</span> Earn after payment</li>
            </ol>
          </div>
          <button type="button" onClick={() => openAuth("signup")} className="navi-ref-cta">
            <span>Get your referral link</span><ArrowUpRight className="w-4 h-4" aria-hidden="true" />
          </button>
        </div>
      </section>

      {/* Upcoming service expansion cities (Admin: content.upcoming_cities) */}
      <UpcomingCities data={upcomingCities} reduced={reducedMotion} />
      </main>

      {/* Compact footer: contact, explore links, and policies. */}
      <footer className="navi-contact-footer relative border-t border-border overflow-hidden">
        <div className="navi-footer-grid navi-wrap px-6 py-8 sm:py-10 grid gap-8 lg:grid-cols-[1fr_1.5fr]">
          <div className="min-w-0">
            <BrandName services style={{ fontSize: Math.max(hero.brand_font_size || 15, 20) }} />
            <p className="mt-3 max-w-sm text-muted-foreground leading-relaxed">{sections.footer_tagline || "Premium doorstep car care for Hyderabad communities."}</p>
            <nav className="navi-footer-explore" aria-label="Explore NAVI CARE">
              <Link to="/partner" data-testid="footer-partner">Franchise</Link>
              <Link to="/careers" data-testid="footer-careers">Careers</Link>
            </nav>
          </div>
          <div className="min-w-0" data-testid="contact-us">
            <h2 className="navi-contact-title">Contact us</h2>
            <div className="navi-contact-cards">
              {contact.phone && <a className="navi-contact-card" href={`tel:${String(contact.phone).replace(/[^\d+]/g, "")}`} data-testid="contact-phone"><span className="navi-contact-ico"><Phone aria-hidden="true" /></span><span><small>Call</small><b>{contact.phone}</b></span></a>}
              {contact.email && <a className="navi-contact-card" href={`mailto:${contact.email}`} data-testid="contact-email"><span className="navi-contact-ico"><Mail aria-hidden="true" /></span><span><small>Email</small><b>{contact.email}</b></span></a>}
              <a className="navi-contact-card is-wa" href={`https://wa.me/${String(contact.whatsapp || "918074422656").replace(/\D/g, "") || "918074422656"}?text=Hi%20Navi%20Car%20Care%2C%20I%20need%20support.`} target="_blank" rel="noreferrer"><span className="navi-contact-ico"><MessageCircle aria-hidden="true" /></span><span><small>WhatsApp</small><b>Chat with support</b></span></a>
              {contact.address && <div className="navi-contact-card is-static"><span className="navi-contact-ico"><MapPin aria-hidden="true" /></span><span><small>Visit</small><b>{contact.address}</b></span></div>}
              {contact.hours && <div className="navi-contact-card is-static" data-testid="contact-hours"><span className="navi-contact-ico"><Clock3 aria-hidden="true" /></span><span><small>Hours</small><b>{contact.hours}</b></span></div>}
            </div>
          </div>
        </div>
        <div className="navi-footer-legal navi-wrap px-6 py-4 border-t border-border flex flex-col items-center gap-1.5 text-xs text-muted-foreground">
          <PolicyLinks />
          <div className="navi-copyright">© {new Date().getFullYear()} {contact.legal_name || "NAVI CARE Services"}. All rights reserved.</div>
        </div>
      </footer>

      {showQrPopup && qrCampaign && (
        <div className="fixed inset-x-4 bottom-5 z-[80] sm:left-auto sm:right-6 sm:max-w-sm" role="dialog" aria-label="NAVI CARE QR welcome">
          <div className="rounded-2xl border border-border bg-background/95 backdrop-blur-xl shadow-2xl p-5">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="text-xs font-semibold uppercase tracking-wider text-primary">NAVI CARE Services</div>
                <div className="mt-1 text-lg font-semibold">Welcome to NAVI CARE 🚗</div>
                <p className="mt-1.5 text-sm text-muted-foreground">Premium doorstep car care for your community.</p>
                {qrCampaign.campaign && <p className="mt-2 text-xs text-muted-foreground">Campaign: {qrCampaign.campaign}</p>}
              </div>
              <button type="button" onClick={() => { localStorage.setItem(`navi_qr_dismissed_${qrCodeParam}`, "1"); setShowQrPopup(false); }} className="p-1 rounded-full hover:bg-secondary" aria-label="Close"><X className="w-4 h-4" /></button>
            </div>
            <div className="mt-4 grid grid-cols-2 gap-2">
              <Button onClick={() => { setShowQrPopup(false); document.getElementById("plans")?.scrollIntoView({behavior: reducedMotion ? "auto" : "smooth", block: "start"}); }} className="rounded-xl">View Plans</Button>
              <Button variant="outline" onClick={() => { setShowQrPopup(false); openAuth("signin"); }} className="rounded-xl">Login / Get Started</Button>
            </div>
          </div>
        </div>
      )}

      <AuthModal
        open={authOpen}
        mode={authMode}
        onModeChange={setAuthMode}
        onClose={closeAuth}
        referralCode={authRef}
        initialEmail={checkoutForm.email}
      />
    </div>
      </>
  );
}
